import { gsap } from 'gsap/dist/gsap.min.js';
import { CustomEase } from 'gsap/dist/CustomEase.min.js';

gsap.registerPlugin(CustomEase);

/** sessionStorage のキー。セッション中1回だけ出す */
const SPLASH_KEY = 'oneview-splashed';

/** 幕が引けるときのカーブ */
const EASE_CURVE = CustomEase.create('splashCurve', 'M0,0 C0.22,0.28 0.28,1 1,1');
/** 幕が引け終わるときの落ち着き */
const EASE_SETTLE = CustomEase.create('splashSettle', 'M0,0 C0.2,1 0.25,1 1,1');
/** FVが持ち上がるときのイージング */
const EASE_LIFT = CustomEase.create('splashLift', '0.65, 0.01, 0.05, 0.99');

/** ロゴを描き終わるまでの尺(秒) */
const DRAW_SEC = 2.2;
/** 次の字を前の字のどこから描き始めるか。0.6なら4割進んだところ */
const DRAW_LAP = 0.6;
/** 描き終わってから幕が動くまでの間(秒) */
const HOLD_SEC = 0.45;
/** 幕の各段の尺(秒) */
const SWEEP_SEC = 1.3;
/** 2段目が始まるまでの遅れ(秒) */
const SWEEP_LAG = 0.65;
/** 1段目で幕の下端が止まる高さ(%) */
const MID_EDGE = 50;
/** 垂れの深さ。幅に対する割合で持つ */
const ARC = 0.69;
/** 幕を画面の外へはみ出させる量(px)。上下左右に地が見えないようにする */
const PAD = 12;
/** FVを何vh下から持ち上げるか */
const LIFT_VH = 20;
/** 幕が動き出してからFVが現れるまでの遅れ(秒) */
const REVEAL_DELAY = 1;

/**
 * 幕の縁が落とす影の、縁からの距離(px)とそこでの濃さ
 *
 * @description ぼかしではなく塗りで作る。iOS Safariはfilterを毎フレームCPUで
 *              焼き直すので、画面いっぱいの幕にfilterを掛けると描き切れずに止まる。
 *              値は drop-shadow(0 28px 64px rgba(7, 7, 7, .22)) を実測して決めてある
 */
const SHADOW_STOPS: { depth: number; alpha: number }[] = [
    { depth: 0, alpha: 0.1325 },
    { depth: 14, alpha: 0.1157 },
    { depth: 26, alpha: 0.1004 },
    { depth: 40, alpha: 0.0811 },
    { depth: 56, alpha: 0.0614 },
    { depth: 72, alpha: 0.0442 },
    { depth: 88, alpha: 0.0326 },
    { depth: 104, alpha: 0.0189 },
    { depth: 128, alpha: 0.009 },
    { depth: 160, alpha: 0.004 },
    { depth: 200, alpha: 0 },
];

/** 影を刻む枚数。濃さの段差が目に見えないところまで細かくする */
const SHADOW_BANDS = 80;

/** 影の1枚。縁からどこまでを塗るかを持つ */
type ShadowBand = { path: SVGPathElement; from: number; to: number };

/**
 * 縁からの距離に対する影の濃さを返す
 *
 * @param {number} depth 縁からの距離(px)
 * @returns {number} 濃さ
 */
const shadowAlphaAt = (depth: number): number => {
    const at = SHADOW_STOPS.findIndex((stop) => stop.depth > depth);

    if (at < 1) return 0;

    const from = SHADOW_STOPS[at - 1];
    const to = SHADOW_STOPS[at];

    return from.alpha + (to.alpha - from.alpha) * ((depth - from.depth) / (to.depth - from.depth));
};

/**
 * 幕の縁が落とす影を組む
 *
 * @description 縁の曲線を深さ違いに入れ子で重ねると、境目ごとに濃さが跳んで縞に見える。
 *              重ならない帯へ刻んで濃さを直接持たせると、段差が刻み幅まで細かくなる。
 *              塗る面積も重ねぶんが消えて1枚ぶんで済む
 * @param {SVGElement} group 影を入れる箱
 * @returns {ShadowBand[]} 組んだ帯
 */
const buildShadow = (group: SVGElement): ShadowBand[] => {
    const deepest = SHADOW_STOPS[SHADOW_STOPS.length - 1].depth;

    return Array.from({ length: SHADOW_BANDS }, (_, i) => {
        const from = (deepest * i) / SHADOW_BANDS;
        const to = (deepest * (i + 1)) / SHADOW_BANDS;
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

        path.setAttribute('fill', '#070707');
        path.setAttribute('fill-opacity', shadowAlphaAt((from + to) / 2).toFixed(4));
        group.appendChild(path);

        return { path, from, to };
    });
};

/**
 * スクロールを無効化する
 *
 * @param {Event} event イベント
 * @returns {void}
 */
const disableScroll = (event: Event): void => {
    event.preventDefault();
};

/**
 * スプラッシュを片付けて通常状態へ戻す
 *
 * @param {HTMLElement} splash スプラッシュ要素
 * @returns {void}
 */
const endSplash = (splash: HTMLElement): void => {
    document.body.classList.remove('splash-is-on');
    document.body.classList.add('splashed');
    document.removeEventListener('touchmove', disableScroll);
    document.removeEventListener('wheel', disableScroll);
    splash.remove();
};

/**
 * スプラッシュを初期化する
 *
 * @returns {void}
 */
const initSplash = (): void => {
    const splash = document.querySelector<HTMLElement>('[data-splash]');

    if (!splash) return; // トップページ以外は要素が無い

    const sheet = splash.querySelector<SVGPathElement>('[data-splash-sheet]');
    const shadowGroup = splash.querySelector<SVGElement>('[data-splash-shadow]');
    const mark = splash.querySelector<SVGElement>('[data-splash-mark]');
    const strokes = Array.from(splash.querySelectorAll<SVGPathElement>('[data-splash-stroke]'));
    const inner = document.querySelector<HTMLElement>('[data-mv-inner]');

    if (!sheet || !shadowGroup || !mark || !strokes.length) {
        splash.remove();
        document.body.classList.add('splashed');
        return;
    }

    // 幕の下端の高さと、カーブの膨らみ。100 で画面を覆い、0 で引け切る
    const st = { edgeY: 100, controlY: 100 };
    const shadows = buildShadow(shadowGroup);

    /**
     * 幕のパスを現在の状態から引き直す
     *
     * @returns {void}
     */
    const draw = (): void => {
        const w = Math.max(1, window.innerWidth);
        const h = Math.max(1, window.innerHeight);
        // 縁がどこまで進むかは高さで測る。抜けきるのは画面の上端なので
        const edgeY = st.edgeY * (h / 100);
        // 垂れは幅で測る。高さで測ると、縦長の画面だけ深いUになる。
        // 弧の見え方を決めるのは「垂れ÷幅」なので、そこを直に持つ
        const ctrlY = edgeY + (st.controlY - st.edgeY) * (w / 100) * ARC;
        const edge = edgeY.toFixed(2);
        const mid = (w / 2).toFixed(2);
        const right = (w + PAD).toFixed(2);
        const curve = `M ${-PAD} ${edge} Q ${mid} ${ctrlY.toFixed(2)} ${right} ${edge}`;

        sheet.setAttribute('d', `${curve} V ${-PAD} H ${-PAD} z`);

        // 縁の曲線を上下へずらして挟んだ帯。隣と重ならないので濃さがそのまま出る
        shadows.forEach(({ path, from, to }) => {
            const top = (edgeY + from).toFixed(2);
            const bottom = (edgeY + to).toFixed(2);

            path.setAttribute(
                'd',
                `M ${-PAD} ${top} Q ${mid} ${(ctrlY + from).toFixed(2)} ${right} ${top}`
                + ` V ${bottom} Q ${mid} ${(ctrlY + to).toFixed(2)} ${-PAD} ${bottom} z`,
            );
        });

        // 幕が引けるほどロゴを薄くして少し上へ逃がす
        const prog = 1 - st.edgeY / 100;

        mark.style.opacity = String(Math.max(0, 1 - prog * 2.2));
        mark.style.translate = `-50% calc(-50% - ${(prog * 18).toFixed(1)}%)`;
    };

    draw();
    // 幕が画面を覆ったので、CSSが敷いていた白はもう要らない
    splash.classList.add('is-drawn');

    document.addEventListener('touchmove', disableScroll, { passive: false });
    document.addEventListener('wheel', disableScroll, { passive: false });
    window.scrollTo(0, 0);

    const tl = gsap.timeline({ onUpdate: draw });

    // ロゴを1文字ずつ描く。字ごとの尺は線の実長に比例させ、等速で引く。
    // そうすると書いていく速さが字によらず揃い、手で書いたように見える
    const lens = strokes.map((el) => el.getTotalLength());
    const sum = lens.reduce((a, b) => a + b, 0) || 1;
    const lastRatio = lens[lens.length - 1] / sum;
    // 次の字を重ねて始めるぶん全体は縮む。DRAW_SECで描き終わるよう1字の尺を逆算する
    const base = DRAW_SEC / Math.max(0.05, 1 - DRAW_LAP + DRAW_LAP * lastRatio);
    let at = 0;

    strokes.forEach((el, i) => {
        const dur = base * (lens[i] / sum);

        tl.set(el, { visibility: 'visible' }, at);
        tl.to(el, { strokeDashoffset: 0, duration: dur, ease: 'none' }, at);
        at += dur * (1 - DRAW_LAP);
    });

    const sweepAt = at + base * lastRatio * DRAW_LAP + HOLD_SEC;

    // 幕は2段で引ける。1段目でカーブを残して半分まで、2段目で膨らみごと引け切る
    tl.to(st, { edgeY: MID_EDGE, controlY: 100, duration: SWEEP_SEC, ease: EASE_CURVE }, sweepAt);
    tl.to(st, { edgeY: 0, controlY: 0, duration: SWEEP_SEC, ease: EASE_SETTLE }, sweepAt + SWEEP_LAG);

    if (inner) {
        tl.fromTo(inner, { y: `${LIFT_VH}vh` }, { y: '0vh', duration: 1, ease: EASE_LIFT }, sweepAt);
    }

    // 写真の出し方はMV側が持っているので、頃合いだけ知らせる
    tl.call(() => document.dispatchEvent(new CustomEvent('splash:reveal')), undefined, sweepAt + REVEAL_DELAY);
    tl.call(() => endSplash(splash));
};

/**
 * スプラッシュを出すかどうかを決める。
 * MV側が初期状態を切り替えるので、DOMContentLoaded より前にbodyへ印を付ける
 *
 * @returns {boolean} 出すなら true
 */
const willPlay = (): boolean => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    if (!document.querySelector('[data-splash]')) return false;

    try {
        if (sessionStorage.getItem(SPLASH_KEY)) return false;
        sessionStorage.setItem(SPLASH_KEY, 'true');
    } catch {
        return false; // プライベートモード等で読み書きできないときは出さない
    }

    return true;
};

if (willPlay()) {
    document.body.classList.add('splash-is-on');

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initSplash);
    else initSplash();
} else {
    document.querySelector('[data-splash]')?.remove();
    document.body.classList.add('splashed');
}
